/**
 * Bubble Component implemented with Material UI
 * 
 * This component provides tooltips and bubble content with customizable styling
 * based on the current UI action.
 */
import React from 'react';
import { styled } from '@mui/material/styles';
import { Tooltip, TooltipProps } from '@mui/material';
import { useSelector } from 'react-redux';
import { muiBubbles as bubbles } from '../molecules/BubbleContent';
import { BubbleProps } from 'types/components/atoms';
import { RootState } from 'types/redux/state';

// Interface for styled tooltip component props
interface StyledTooltipProps extends Omit<TooltipProps, 'classes' | 'title'> {
  hide?: number;
  className?: string;
  title?: React.ReactNode;
}

/**
 * Bubble component
 * 
 * @param props - Component props
 * @returns A tooltip-style bubble component
 */
const Bubble: React.FC<BubbleProps> = ({
  arrow = false,
  placement = 'top',
  enterDelay = 100,
  leaveDelay = 0,
  className = "custom-tooltip",
  ...props
}) => {
  const currentAction = useSelector((state: RootState) => state.ui.action);
  const Element = bubbles[currentAction]?.element;
  const hasContent = !useSelector((state: RootState) => state.ui.bubbleContent);

  return (
    <StyledTooltip 
      arrow={arrow}
      placement={placement}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      hide={hasContent ? 1 : 0}
      className={className}
      {...props}
    >
      <div>{Element && <Element />}</div>
    </StyledTooltip>
  );
};

// Styled components using MUI styled API
const StyledTooltip = styled(
  ({ hide, className, ...props }: StyledTooltipProps) => (
    <Tooltip {...props as TooltipProps} classes={{ tooltip: className }} />
  )
)(({ theme, hide }: { theme: any, hide?: number }) => ({
  '&.custom-tooltip': {
    visibility: hide ? 'hidden' : 'visible',
    backgroundColor: 'var(--app-dark-gray)',
    color: '#fff',
    opacity: 0.8,
    zIndex: 10,
    padding: '4px 8px',
  },
  '&.button-tooltip': {
    backgroundColor: 'var(--app-dark-gray)',
    color: '#fff',
    opacity: 0.8,
    zIndex: 10,
    padding: '4px 8px',
  },
}));

export type { BubbleProps };
export default Bubble;
